import React, { useState, useEffect } from 'react';
import { Col, Container, Row, Button, Spinner } from 'reactstrap';
import { Link, useParams } from 'react-router-dom';
import MetaTags from "react-meta-tags";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ApiClient from '../BaseUrl/ApiClient';
import './LTDealDetail.css';

const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

const getDuration = (start, end) => {
    if (!start || !end) return null;
    const s = new Date(start);
    const e = new Date(end);
    if (isNaN(s.getTime()) || isNaN(e.getTime())) return null;
    const days = Math.round((e - s) / (1000 * 60 * 60 * 24));
    if (days < 0) return null;
    return days === 0 ? '1 day' : `${days} day${days > 1 ? 's' : ''}`;
};

function LTDealDetail() {
    const { dealId } = useParams();
    const [deal, setDeal] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchDealDetails = async () => {
        setLoading(true);
        setError(null);
        try {
            const client = ApiClient();
            const response = await client.get(`business/deal/limitedTime/${dealId}`);
            const fetchedDeal = response.data.responseData.deal;
            setDeal(fetchedDeal);
        } catch (err) {
            setError('Failed to fetch deal details. Please try again.');
            toast.error('Failed to fetch deal details.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (dealId) {
            fetchDealDetails();
        }
    }, [dealId]);

    if (loading) {
        return (
            <div className="page-content">
                <Container fluid className="mt-5">
                    <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '320px' }}>
                        <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
                        <h5 className="mt-3 text-muted">Loading Deal Details...</h5>
                    </div>
                </Container>
            </div>
        );
    }

    if (error || !deal) {
        return (
            <div className="page-content">
                <Container fluid className="mt-5">
                    <div className="text-center">
                        <lord-icon
                            src="https://cdn.lordicon.com/msoeawqm.json"
                            trigger="loop"
                            colors="secondary:#121331,secondary:#08a88a"
                            style={{ width: '75px', height: '75px' }}
                        ></lord-icon>
                        <h5 className="mt-2">Sorry! No Deal Found</h5>
                        <p className="text-muted mb-0">{error || 'No deal data available.'}</p>
                    </div>
                </Container>
            </div>
        );
    }

    const duration = getDuration(deal.startDate, deal.endDate);

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid className="mt-5">
                    <MetaTags>
                        <title>Limited Time Deal Detail</title>
                    </MetaTags>
                    <ToastContainer />

                    <div className="mb-3">
                        <Link to="/limited-time-deal-list">
                            <Button color="primary" className="ltd-back-btn">
                                <i className="mdi mdi-arrow-left"></i> Back to Deals
                            </Button>
                        </Link>
                    </div>

                    <div className="ltd-detail-card">
                        <Row className="g-0">
                            <Col lg="5" md="5">
                                <div className="ltd-image-wrap">
                                    <span className={`ltd-active-badge ${deal.isActive ? 'active' : 'inactive'}`}>
                                        <span className="dot"></span>
                                        {deal.isActive ? 'Available' : 'Unavailable'}
                                    </span>
                                    {deal.dealPhoto?.url ? (
                                        <img
                                            src={deal.dealPhoto.url}
                                            alt={deal.name}
                                            className="ltd-image"
                                        />
                                    ) : (
                                        <span className="ltd-image-placeholder">No image available</span>
                                    )}
                                </div>
                            </Col>

                            <Col lg="7" md="7">
                                <div className="ltd-body">
                                    <span className="ltd-eyebrow">
                                        <i className="mdi mdi-clock-fast"></i> Limited Time Deal
                                    </span>
                                    <h1 className="ltd-title">{deal.name}</h1>

                                    <div className="ltd-info-grid">
                                        <div className="ltd-info-tile">
                                            <div className="ltd-info-icon cat">
                                                <i className="mdi mdi-tag-outline"></i>
                                            </div>
                                            <div className="ltd-info-meta">
                                                <span className="ltd-info-label">Category</span>
                                                <span className="ltd-info-value">
                                                    {deal.category?.categoryLogo?.url && (
                                                        <img
                                                            src={deal.category.categoryLogo.url}
                                                            alt={deal.category?.name}
                                                            className="ltd-cat-logo"
                                                        />
                                                    )}
                                                    {deal.category?.name || 'N/A'}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="ltd-info-tile">
                                            <div className="ltd-info-icon start">
                                                <i className="mdi mdi-calendar-start"></i>
                                            </div>
                                            <div className="ltd-info-meta">
                                                <span className="ltd-info-label">Start Date</span>
                                                <span className="ltd-info-value">{formatDate(deal.startDate)}</span>
                                            </div>
                                        </div>

                                        <div className="ltd-info-tile">
                                            <div className="ltd-info-icon end">
                                                <i className="mdi mdi-calendar-end"></i>
                                            </div>
                                            <div className="ltd-info-meta">
                                                <span className="ltd-info-label">End Date</span>
                                                <span className="ltd-info-value">{formatDate(deal.endDate)}</span>
                                            </div>
                                        </div>

                                        {duration && (
                                            <div className="ltd-info-tile">
                                                <div className="ltd-info-icon duration">
                                                    <i className="mdi mdi-timer-sand"></i>
                                                </div>
                                                <div className="ltd-info-meta">
                                                    <span className="ltd-info-label">Duration</span>
                                                    <span className="ltd-info-value">{duration}</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {deal.description && (
                                        <div>
                                            <div className="ltd-section-title">Description</div>
                                            <div className="ltd-description">{deal.description}</div>
                                        </div>
                                    )}
                                </div>
                            </Col>
                        </Row>
                    </div>
                </Container>
            </div>
        </React.Fragment>
    );
}

export default LTDealDetail;
